import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import RestoreRoundedIcon from "@mui/icons-material/RestoreRounded";
import { Box, Fade, Menu, MenuItem, Skeleton } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useEffect, useId, useRef, useState } from "react";
import { ALPHA, EVENT_LIST, FONT_SIZE, INK, LINE_HEIGHT, MOTION, NEUTRAL, SHADOW, SURFACE, VERSION_HISTORY } from "../../../theme/tokens";
import MetaChip from "../Badges/MetaChip";
import { EyebrowLabel } from "../molecules/EyebrowLabel";
import { portalOverlayProps } from "./portalOverlay";
import { versionRowSx } from "./versionHistoryMenuSx";

/** One saved version of a record, every string already translated and formatted by the caller. */
export type VersionHistoryEntry = {
	id: string;
	/** "Version 4". */
	title: string;
	/** What changed in it, clamped to `VERSION_HISTORY.SUMMARY_LINES`. Absent = none. */
	summary?: string;
	/** Who saved it and when ("Dana Levi · Today, 3:14 PM"). Absent = none. */
	meta?: string;
	/** Where it came from, beside a restore glyph ("Restored from version 2"). Absent = none. */
	note?: string;
	/** A small badge after the title ("Current"). Absent = none. */
	badge?: string;
	/** The version in use: its marker is filled and haloed, the rest are rings. */
	isLive?: boolean;
	dataTestId: string;
};

type Props = {
	/** Newest first. `undefined` until the history has loaded. */
	entries: VersionHistoryEntry[] | undefined;
	status: "pending" | "error" | "success";
	/** The version on screen: washed, checked and announced as checked. */
	shownId: string | null;
	/** A version was picked. Runs once the menu has closed and given focus back to the trigger. */
	onSelect: (id: string) => void;
	/** The menu opened or closed — load or refresh the history on open. */
	onOpenChange?: (open: boolean) => void;
	/** The trigger's text: which version is on screen, short enough for a row of actions ("v4"). */
	triggerLabel: string;
	/** The trigger's tooltip and accessible name, in full ("Version history, showing version 4"). */
	triggerAriaLabel: string;
	/** "Version history": the panel's heading, which names the menu. */
	title: string;
	/** One quiet line under the heading — what picking a version does. Absent = none. */
	hint?: string;
	/** Shown, as a row that retries, when the history couldn't load. */
	errorLabel: string;
	onRetry: () => void;
	/** An earlier version is on screen: the trigger takes the brand tone, so the state is visible while closed. */
	active?: boolean;
	/** Trigger test id; the panel is `{dataTestId}-menu`, the error row `{dataTestId}-retry`. */
	dataTestId: string;
};

const SKELETON_ROWS = 3;

/** The physical side the panel opens toward: into the page, away from the trigger's end of the row. */
const openingSide = (trigger: HTMLElement | null): "left" | "right" => (trigger !== null && getComputedStyle(trigger).direction === "rtl" ? "right" : "left");

/**
 * A record's saved versions as a timeline menu: a chip naming the version on screen ("Version 4", with a history
 * glyph) opens the list, newest first — each version a marker on a rail, its title and badge, what changed, who
 * saved it when, and where it came from. Picking one hands its id back once the menu has closed; the caller decides
 * what viewing it means. The live version's marker is filled and haloed; the one on screen is washed and checked.
 *
 * MUI `Menu` underneath, so it stacks correctly inside a modal (its own focus trap, Escape, focus back to the chip)
 * and the arrow keys move between versions. Loading shows skeleton rows; a failed load is a row that retries.
 */
export const VersionHistoryMenu: FC<Props> = (props) => {
	const { entries, status, shownId, onSelect, onOpenChange, triggerLabel, triggerAriaLabel, title, hint, active, dataTestId } = props;
	const menuId = useId();
	const titleId = useId();
	const triggerRef = useRef<HTMLSpanElement>(null);
	const [open, setOpen] = useState(false);
	const pending = useRef<string | null>(null);

	// After the close has committed: focus is back on the chip, so anything the pick opens returns focus there.
	useEffect(() => {
		if (open || pending.current === null) return;
		const picked = pending.current;
		pending.current = null;
		onSelect(picked);
	}, [open, onSelect]);

	const setMenuOpen = (next: boolean) => {
		setOpen(next);
		onOpenChange?.(next);
	};
	const pick = (id: string) => {
		pending.current = id;
		setMenuOpen(false);
	};
	const side = openingSide(triggerRef.current);
	const rows = entries === undefined ? [] : entries;

	return (
		<>
			<MetaChip
				ref={triggerRef}
				label={triggerLabel}
				icon={<HistoryRoundedIcon />}
				tone={active === true ? "primary" : "neutral"}
				onClick={() => setMenuOpen(!open)}
				ariaLabel={triggerAriaLabel}
				ariaHasPopup="menu"
				ariaExpanded={open}
				tooltip={triggerAriaLabel}
				tooltipHidden={open}
				dataTestId={dataTestId}
			/>
			<Menu
				id={menuId}
				anchorEl={triggerRef.current}
				open={open}
				onClose={() => setMenuOpen(false)}
				anchorOrigin={{ vertical: "bottom", horizontal: side }}
				transformOrigin={{ vertical: "top", horizontal: side }}
				slots={{ transition: Fade }}
				transitionDuration={{ enter: MOTION.DURATION.ENTRANCE_SM, exit: MOTION.DURATION.INLINE_EXIT }}
				slotProps={{
					root: { ...portalOverlayProps("version-history-menu") },
					paper: {
						// A data attribute rides the slot props through MUI's spread; the slot type has no index signature for it.
						...({ "data-testid": `${dataTestId}-menu` } as Record<string, string>),
						sx: {
							mt: 0.75,
							width: VERSION_HISTORY.PANEL_WIDTH_PX,
							maxWidth: (theme) => `calc(100vw - ${theme.spacing(4)})`,
							maxHeight: VERSION_HISTORY.MAX_HEIGHT_PX,
							borderRadius: 1.5,
							border: `1px solid ${NEUTRAL[200]}`,
							boxShadow: SHADOW.POPOVER_FLOAT,
							"@keyframes versionHistoryRise": { from: { transform: "translateY(-4px) scale(0.985)" }, to: { transform: "none" } },
							animation: `versionHistoryRise ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
							"@media (prefers-reduced-motion: reduce)": { animation: "none" },
						},
					},
					list: { sx: { py: 0.5 }, "aria-labelledby": titleId },
				}}
			>
				<Box component="li" role="presentation" sx={{ px: 2, pt: 1, pb: 1 }}>
					<EyebrowLabel id={titleId} component="span" sx={{ display: "block" }}>
						{title}
					</EyebrowLabel>
					{hint !== undefined && (
						<Box component="span" sx={{ display: "block", mt: 0.5, fontSize: FONT_SIZE.CAPTION, lineHeight: LINE_HEIGHT.SNUG, color: INK.MUTED }}>
							{hint}
						</Box>
					)}
				</Box>
				{status === "pending" &&
					Array.from({ length: SKELETON_ROWS }, (_unused, index) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: fixed placeholder rows
						<Box key={index} component="li" role="presentation" aria-hidden sx={{ px: 2, py: 1 }}>
							<Skeleton variant="text" width="40%" />
							<Skeleton variant="text" width="90%" />
						</Box>
					))}
				{status === "error" && (
					<MenuItem
						onClick={props.onRetry}
						data-testid={`${dataTestId}-retry`}
						sx={{ mx: 0.5, gap: 1, borderRadius: 1, whiteSpace: "normal", fontSize: FONT_SIZE.SECONDARY, color: "warning.dark" }}
					>
						<RefreshRoundedIcon sx={{ fontSize: FONT_SIZE.TITLE_SM }} aria-hidden />
						{props.errorLabel}
					</MenuItem>
				)}
				{status === "success" &&
					rows.map((entry, index) => (
						<VersionRow key={entry.id} entry={entry} isShown={entry.id === shownId} isFirst={index === 0} isLast={index === rows.length - 1} onPick={pick} />
					))}
			</Menu>
		</>
	);
};

type RowProps = { entry: VersionHistoryEntry; isShown: boolean; isFirst: boolean; isLast: boolean; onPick: (id: string) => void };

/** One version on the timeline: its marker on the rail, then title + badge, summary, who and when, and its note. */
const VersionRow: FC<RowProps> = ({ entry, isShown, isFirst, isLast, onPick }) => (
	<MenuItem
		role="menuitemradio"
		aria-checked={isShown}
		selected={isShown}
		onClick={() => onPick(entry.id)}
		data-testid={entry.dataTestId}
		sx={versionRowSx({ isFirst, isLast })}
	>
		<Box className="version-marker" aria-hidden>
			<Box
				sx={(theme) => ({
					width: VERSION_HISTORY.MARKER_PX,
					height: VERSION_HISTORY.MARKER_PX,
					borderRadius: "50%",
					boxSizing: "border-box",
					border: `${VERSION_HISTORY.MARKER_RING_PX}px solid ${entry.isLive === true ? theme.palette.primary.main : NEUTRAL[300]}`,
					backgroundColor: entry.isLive === true ? theme.palette.primary.main : SURFACE,
					boxShadow: entry.isLive === true ? `0 0 0 ${EVENT_LIST.EMPHASIS_RING_PX}px ${alpha(theme.palette.primary.main, ALPHA.CONTROL_TINT_HOVER)}` : "none",
				})}
			/>
		</Box>
		<Box sx={{ flex: 1, minWidth: 0 }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minHeight: "var(--version-title-line)" }}>
				<Box
					component="span"
					sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, lineHeight: LINE_HEIGHT.TIGHT, color: isShown ? "primary.dark" : INK.PRIMARY }}
				>
					{entry.title}
				</Box>
				{entry.badge !== undefined && <MetaChip label={entry.badge} tone="primary" dataTestId={`${entry.dataTestId}-badge`} />}
				{isShown && <CheckRoundedIcon aria-hidden sx={{ marginInlineStart: "auto", fontSize: FONT_SIZE.TITLE_SM, color: "primary.main" }} />}
			</Box>
			{entry.summary !== undefined && (
				<Box
					sx={{
						mt: 0.25,
						fontSize: FONT_SIZE.CAPTION,
						lineHeight: LINE_HEIGHT.SNUG,
						color: INK.SECONDARY,
						display: "-webkit-box",
						WebkitLineClamp: VERSION_HISTORY.SUMMARY_LINES,
						WebkitBoxOrient: "vertical",
						overflow: "hidden",
					}}
				>
					{entry.summary}
				</Box>
			)}
			{entry.meta !== undefined && <Box sx={{ mt: 0.375, fontSize: FONT_SIZE.LABEL_SM, color: INK.MUTED }}>{entry.meta}</Box>}
			{entry.note !== undefined && (
				<Box sx={{ mt: 0.375, display: "flex", alignItems: "center", gap: 0.5, fontSize: FONT_SIZE.LABEL_SM, color: "primary.dark" }}>
					<RestoreRoundedIcon aria-hidden sx={{ fontSize: FONT_SIZE.EMPHASIS }} />
					{entry.note}
				</Box>
			)}
		</Box>
	</MenuItem>
);
